// src/components/surveys/questions/DropdownQuestion.tsx
import React from 'react';
import { SurveyQuestion } from '../../../types/survey.types';
import { ChevronDown } from 'lucide-react';

interface DropdownQuestionProps {
  question: SurveyQuestion;
  value?: string | null;
  onChange?: (optionId: string) => void;
  disabled?: boolean;
  showValidation?: boolean;
  error?: string;
  mode?: 'edit' | 'preview' | 'response';
}

const DropdownQuestion: React.FC<DropdownQuestionProps> = ({
  question,
  value,
  onChange,
  disabled = false,
  showValidation = false,
  error,
  mode = 'response',
}) => {
  const options = question.options || [];

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (onChange && e.target.value) {
      onChange(e.target.value);
    }
  };

  if (mode === 'edit') {
    return (
      <div className="space-y-2">
        <div className="text-sm text-gray-500">Dropdown preview:</div>
        <div className="relative">
          <select
            className="w-full p-3 border border-gray-200 rounded-lg bg-gray-50 appearance-none"
            disabled
          >
            <option value="">Select an option...</option>
            {options.map((option, index) => (
              <option key={option.id} value={option.id}>
                {option.text || `Option ${index + 1}`}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
        </div>
        <div className="text-xs text-gray-400">
          {options.length} options configured
        </div>
      </div>
    );
  }

  const selectedOption = options.find((opt) => opt.id === value);

  return (
    <div className="space-y-2">
      <div className="relative">
        <select
          value={value || ''}
          onChange={handleChange}
          disabled={disabled}
          className={`w-full p-3 border-2 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
            error
              ? 'border-red-300 focus:border-red-500'
              : value
              ? 'border-indigo-500 bg-indigo-50'
              : 'border-gray-200'
          } ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'bg-white cursor-pointer'}`}
        >
          <option value="">Select an option...</option>
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.text}
            </option>
          ))}
        </select>
        <ChevronDown
          className={`absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${
            value ? 'text-indigo-500' : 'text-gray-400'
          }`}
        />
      </div>
      {showValidation && error && (
        <p className="text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default DropdownQuestion;
